/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 **/

import { Component } from 'react'
import { Button, Text, View, Loader } from '@az/starc-ui'
import { Location } from '@az/starc-ui-icons'
import { t } from 'i18next'
import { APIStore } from '../../types'
import ItemLookupDataService from '../../services/ItemLookupDataService'
import style from './StorePosition.module.css'

type Props = {
  setStore: (value: string) => void
}

type State = {
  store: string
  latitude?: number
  longitude?: number
  loading?: boolean
  error?: string
}

class StorePositionScreen extends Component<Props, State> {
  constructor(props: any) {
    super(props)
    this.state = {
      store: '',
      latitude: undefined,
      longitude: undefined,
      loading: false,
      error: undefined
    }
  }

  private handlePositionSuccess: PositionCallback = async position => {
    try {
      const response: any = await ItemLookupDataService.getStoreByLocation(
        position.coords.latitude,
        position.coords.longitude
      )

      const storeData: APIStore = response.data
      this.setState(
        {
          store: storeData.storeId,
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          loading: false,
          error: undefined
        },
        this.updateSessionStore // ✅ Reference to class method
      )
    } catch (e) {
      this.setState({
        loading: false,
        error: 'Fetching store location failed! Please input store number.'
      })
      /* eslint-disable-next-line no-console */
      console.error('Error getting store by location', e)
    }
  }

  private updateSessionStore = () => {
    sessionStorage.setItem('store', this.state.store)
    this.props.setStore(this.state.store)
  }

  private handlePositionError: PositionErrorCallback = error => {
    this.setState({
      loading: false,
      error: 'Fetching store location failed! Please input store number.'
    })
    /* eslint-disable-next-line no-console */
    console.error('Error getting store by location', error.message)
  }

  getPosition = () => {
    this.setState({ loading: true })

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        this.handlePositionSuccess,
        this.handlePositionError,
        { timeout: 5000 }
      )
    } else {
      this.setState({
        loading: false,
        error: 'Geolocation is not supported! Please input store number.'
      })
      /* eslint-disable-next-line no-console */
      console.error('Geolocation is not supported by this browser.')
    }
  }

  render() {
    const { loading, error } = this.state

    return (
      <article className={'article'}>
        <View className={style.locationContainer}>
          <View direction="row" width="100%">
            <View.Item grow columns={12}>
              <Text align="center" className={style.locationStoreText}>
                {t('store-search') as string}
              </Text>
            </View.Item>
          </View>
          <View direction="row" width="100%" align="center">
            <View.Item grow className={style.locationButtonContainer}>
              <Button
                disabled={loading}
                attributes={{ id: 'position-button' }}
                className={style.locationButton}
                onClick={this.getPosition}
                startIcon={Location}
                iconColor="primary"
                data-cy="geo-position-entry-button"
              >
                <Text align="end" className={style.locationText}>
                  {t('use-my-location') as string}
                </Text>
              </Button>
            </View.Item>
          </View>
          {loading && (
            <View direction="row" width="100%" align="center" justify="center">
              <View.Item
                className={style.SearchFullSize}
                attributes={{ style: { marginTop: '10px', textAlign: 'center' } }}
              >
                <Loader size="large" variant="spinner" />
              </View.Item>
            </View>
          )}
          {error && (
            <View direction="row" width="100%" align="center" justify="center">
              <View.Item className={style.SearchFullSize}>
                <Text variant="small-body" size="150" color="error" align="center">
                  {error}
                </Text>
              </View.Item>
            </View>
          )}
        </View>
      </article>
    )
  }
}

export default StorePositionScreen
